import { useState, useRef } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Camera, Upload, X, Trash2, FileText, Plus } from "lucide-react";
import { toast } from "sonner";
import { jsPDF } from "jspdf";

// Simple multi-page scanner: capture/attach pages, then combine into one PDF.
const ScanModal = ({ onClose, onPdfReady }) => {
  const [pages, setPages] = useState([]); // { url, file }
  const [building, setBuilding] = useState(false);
  const cameraRef = useRef();
  const fileRef = useRef();

  const addFiles = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const imgs = files.filter(f => f.type.startsWith("image/"));
    if (!imgs.length) {
      toast.error("Only image files can be scanned");
      return;
    }
    setPages(prev => [...prev, ...imgs.map(f => ({ url: URL.createObjectURL(f), file: f }))]);
    e.target.value = "";
  };

  const removePage = (idx) => {
    setPages(prev => {
      URL.revokeObjectURL(prev[idx].url);
      return prev.filter((_, i) => i !== idx);
    });
  };

  const loadImage = (url) => new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });

  // Normalize one page: fix phone-camera EXIF rotation, cap size, mild document cleanup.
  // Returns { dataUrl, width, height } with correct (un-stretched) proportions.
  const processPage = async (file) => {
    let bitmap = null;
    try {
      bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
    } catch {
      bitmap = null;
    }
    const srcW = bitmap ? bitmap.width : 0;
    const srcH = bitmap ? bitmap.height : 0;
    if (bitmap && srcW && srcH) {
      const maxEdge = 2000; // sharp enough for A4 print, keeps PDF size sane
      const scale = Math.min(1, maxEdge / Math.max(srcW, srcH));
      const w = Math.max(1, Math.round(srcW * scale));
      const h = Math.max(1, Math.round(srcH * scale));
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      ctx.filter = "contrast(1.08) brightness(1.06)"; // light scan cleanup
      ctx.drawImage(bitmap, 0, 0, w, h);
      bitmap.close();
      return { dataUrl: canvas.toDataURL("image/jpeg", 0.9), width: w, height: h };
    }
    // Fallback: plain image element drawn through the same canvas path (older browsers)
    const objUrl = URL.createObjectURL(file);
    try {
      const img = await loadImage(objUrl);
      const maxEdge = 2000;
      const scale = Math.min(1, maxEdge / Math.max(img.width, img.height));
      const w = Math.max(1, Math.round(img.width * scale));
      const h = Math.max(1, Math.round(img.height * scale));
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      ctx.filter = "contrast(1.08) brightness(1.06)";
      ctx.drawImage(img, 0, 0, w, h);
      return { dataUrl: canvas.toDataURL("image/jpeg", 0.9), width: w, height: h };
    } finally {
      URL.revokeObjectURL(objUrl);
    }
  };

  const buildPdf = async () => {
    if (!pages.length) {
      toast.error("Add at least one page first");
      return;
    }
    setBuilding(true);
    try {
      const pdf = new jsPDF({ unit: "mm", format: "a4" });
      const pageW = 210, pageH = 297, margin = 8;
      for (let i = 0; i < pages.length; i++) {
        const page = await processPage(pages[i].file);
        const maxW = pageW - margin * 2, maxH = pageH - margin * 2;
        const ratio = Math.min(maxW / page.width, maxH / page.height);
        const w = page.width * ratio, h = page.height * ratio;
        const x = (pageW - w) / 2, y = (pageH - h) / 2;
        if (i > 0) pdf.addPage();
        pdf.addImage(page.dataUrl, "JPEG", x, y, w, h);
      }
      const blob = pdf.output("blob");
      const stamp = new Date().toISOString().slice(0, 10);
      const file = new File([blob], `Scan-${stamp}-${Date.now() % 100000}.pdf`, { type: "application/pdf" });
      onPdfReady(file);
    } catch (err) {
      console.error(err);
      toast.error("Could not build the PDF");
    } finally {
      setBuilding(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
      data-testid="scan-modal"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="trade-card rounded-sm p-6 w-full max-w-2xl max-h-[85vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#D4AF37]/10 rounded-sm flex items-center justify-center">
              <FileText className="w-5 h-5 trade-gold" strokeWidth={1.5} />
            </div>
            <div>
              <h3 className="font-serif font-semibold text-lg">Scan Document</h3>
              <p className="text-xs text-white/50">{pages.length} page{pages.length !== 1 ? "s" : ""} — saved as one PDF</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-white/40 hover:text-white transition-colors" data-testid="scan-close-btn">
            <X size={18} />
          </button>
        </div>

        <input ref={cameraRef} type="file" accept="image/*" capture="environment" onChange={addFiles} className="hidden" data-testid="scan-camera-input" />
        <input ref={fileRef} type="file" accept="image/*" multiple onChange={addFiles} className="hidden" data-testid="scan-file-input" />

        <div className="flex flex-col sm:flex-row gap-3 mb-5">
          <Button
            onClick={() => cameraRef.current?.click()}
            className="flex-1 trade-btn-primary rounded-sm h-11"
            data-testid="scan-add-page-btn"
          >
            <Camera size={16} className="mr-2" /> Scan Page (Camera)
          </Button>
          <Button
            onClick={() => fileRef.current?.click()}
            className="flex-1 bg-white/10 text-white hover:bg-white/20 rounded-sm h-11"
            data-testid="scan-attach-btn"
          >
            <Upload size={16} className="mr-2" /> Attach Photos
          </Button>
        </div>

        {pages.length === 0 ? (
          <div className="border border-dashed border-white/10 rounded-sm p-10 text-center text-white/40 text-sm">
            No pages yet — scan or attach the first page
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-5" data-testid="scan-pages-grid">
            {pages.map((p, i) => (
              <div key={p.url} className="relative group" data-testid={`scan-page-${i}`}>
                <img src={p.url} alt={`Page ${i + 1}`} className="w-full h-32 object-cover rounded-sm bg-white" />
                <span className="absolute top-1 left-1 bg-black/70 text-white text-[10px] px-2 py-0.5 rounded-sm">Page {i + 1}</span>
                <button
                  onClick={() => removePage(i)}
                  className="absolute top-1 right-1 p-1.5 bg-red-500/80 text-white rounded-sm opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity"
                  data-testid={`scan-remove-${i}`}
                >
                  <Trash2 size={12} />
                </button>
              </div>
            ))}
            <button
              onClick={() => cameraRef.current?.click()}
              className="h-32 border border-dashed border-white/15 rounded-sm flex flex-col items-center justify-center gap-1 text-white/40 hover:text-[#D4AF37] hover:border-[#D4AF37]/40 transition-colors text-xs"
              data-testid="scan-add-more-btn"
            >
              <Plus size={18} /> Add page
            </button>
          </div>
        )}

        <Button
          onClick={buildPdf}
          disabled={building || pages.length === 0}
          className="w-full trade-btn-primary rounded-sm h-12"
          data-testid="scan-save-pdf-btn"
        >
          <FileText size={16} className="mr-2" />
          {building ? "Building PDF..." : `Save ${pages.length || ""} Page${pages.length !== 1 ? "s" : ""} as PDF`}
        </Button>
      </motion.div>
    </div>
  );
};

export default ScanModal;
